/**
 * CR-7168: A single Appeals table row.
 *
 * Renders cells dynamically based on the visible column configuration,
 * following the DeliveryRow pattern. Cell content is preserved from the
 * original AppealsSubTab inline implementation.
 */
import type { ReactNode } from 'react';
import { Eye } from 'lucide-react';
import PoLineLink from '../../../common/PoLineLink';
import type { DeliveryAppeal } from '../../../../lib/api';
import type { AppealColumnConfig } from './appealsColumnConfig';
import type { AppealBulkRespond } from '../delivery/useAppealBulkRespond';
import { getOntimeStatusBadge } from '../delivery/deliveryBadges';
import AppealInlineActions from '../delivery/AppealInlineActions';

interface AppealRowProps {
  appeal: DeliveryAppeal;
  visibleColumns: AppealColumnConfig[];
  formatDate: (dateString: string | undefined) => string;
  getAppealStatusBadge: (status: string, isCustomerAppeal?: boolean, workflowStatus?: string) => ReactNode;
  onViewGrn: (grnId: number) => void;
  onRespondAppeal: (appealId: number) => void;
  /** Bulk respond state - if provided, renders leading checkbox cell for eligible appeals. */
  bulk?: AppealBulkRespond;
  /** CR-7169: customer-side viewer — only they get Accept/Reject on the row. */
  canRespond?: boolean;
  /** CR-7169: bump the parent's refresh counter after an inline decision.
   *  Required, like AppealInlineActions itself — a row whose Accept/Reject
   *  does not refresh the list leaves a stale decision on screen. */
  onResponded: () => void;
}

const CB = 'h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer';

/**
 * Renders one Appeals table row with only the visible columns in order.
 */
export default function AppealRow({
  appeal,
  visibleColumns,
  formatDate,
  getAppealStatusBadge,
  onViewGrn,
  onRespondAppeal,
  bulk,
  canRespond = false,
  onResponded,
}: AppealRowProps) {
  const renderCell = (colId: string): ReactNode => {
    switch (colId) {
      case 'appeal_id':
        return <td key={colId} className="py-3 px-4 font-medium">{appeal.id}</td>;
      case 'grn_number':
        return (
          <td key={colId} className="py-3 px-4">
            <button onClick={() => onViewGrn(appeal.grn)} className="text-blue-600 hover:text-blue-800 hover:underline font-medium">
              {appeal.grn_number}
            </button>
          </td>
        );
      case 'po_number':
        return <td key={colId} className="py-3 px-4">{appeal.po_number}</td>;
      case 'line_number':
        return (
          <td key={colId} className="py-3 px-4">
            <PoLineLink poLineId={appeal.po_line_id}>{appeal.line_number}</PoLineLink>
          </td>
        );
      case 'part_number':
        return <td key={colId} className="py-3 px-4 font-mono">{appeal.part_number}</td>;
      case 'supplier_name':
        return <td key={colId} className="py-3 px-4">{appeal.supplier_name || '-'}</td>;
      case 'status':
        return <td key={colId} className="py-3 px-4">{getAppealStatusBadge(appeal.status, appeal.is_customer_appeal)}</td>;
      case 'on_time_status':
        return (
          <td key={colId} className="py-3 px-4">
            {appeal.on_time_status ? getOntimeStatusBadge(appeal.on_time_status) : <span className="text-gray-400">-</span>}
          </td>
        );
      case 'otif_overridden':
        return (
          <td key={colId} className="py-3 px-4">
            {appeal.otif_overridden ? (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 border border-teal-300">Overridden</span>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </td>
        );
      case 'grn_mrp_date':
        return <td key={colId} className="py-3 px-4">{appeal.grn_mrp_date ? formatDate(appeal.grn_mrp_date) : '-'}</td>;
      case 'grn_review_date':
        return <td key={colId} className="py-3 px-4">{appeal.grn_review_date ? formatDate(appeal.grn_review_date) : '-'}</td>;
      case 'grn_original_delivery_date':
        return <td key={colId} className="py-3 px-4">{appeal.grn_original_delivery_date ? formatDate(appeal.grn_original_delivery_date) : '-'}</td>;
      case 'grn_receipt_date':
        return <td key={colId} className="py-3 px-4">{appeal.grn_receipt_date ? formatDate(appeal.grn_receipt_date) : '-'}</td>;
      case 'supplier_note':
        return <td key={colId} className="py-3 px-4 max-w-[200px] truncate" title={appeal.supplier_note}>{appeal.supplier_note || '-'}</td>;
      case 'created_at':
        return <td key={colId} className="py-3 px-4">{formatDate(appeal.created_at)}</td>;
      default:
        return null;
    }
  };

  return (
    <tr className="border-b hover:bg-muted/50 transition-colors">
      {bulk?.enabled && (
        <td className="py-3 px-4">
          {bulk.isEligible(appeal) ? (
            <input type="checkbox" checked={bulk.selectedIds.has(appeal.id)} onChange={() => bulk.toggle(appeal.id)} className={CB} title="Select this appeal" />
          ) : null}
        </td>
      )}
      {visibleColumns.map((col) => renderCell(col.id))}
      {/* View + Action columns are always visible and pinned right */}
      <td className="py-3 px-4 sticky right-[240px] bg-background z-10 min-w-[100px]">
        <button onClick={() => onViewGrn(appeal.grn)} className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-sm" title="View delivery details">
          <Eye className="h-4 w-4" /> View
        </button>
      </td>
      {/* CR-7169: Accept/Reject act from the row; Respond opens the form.
          This column keeps its 240px width — the plain Respond button this
          replaced was 100px, written before CR-7169 landed. */}
      <td className="py-3 px-4 sticky right-0 bg-background z-10 min-w-[240px]">
        <AppealInlineActions
          appeal={appeal}
          canRespond={canRespond}
          onRespond={onRespondAppeal}
          onResponded={onResponded}
        />
      </td>
    </tr>
  );
}
